import {
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  untrack,
} from 'solid-js';
import { Desktop } from './components/Desktop';
import { StartMenu } from './components/StartMenu';
import { Taskbar } from './components/Taskbar';
import { WindowFrame } from './components/WindowFrame';
import { AboutWindow } from './components/windows/AboutWindow';
import { ProjectExplorerWindow } from './components/windows/ProjectExplorerWindow';
import { ProjectWindow } from './components/windows/ProjectWindow';
import { RecycleBinWindow } from './components/windows/RecycleBinWindow';
import { SettingsWindow } from './components/windows/SettingsWindow';
import { ShutdownWindow } from './components/windows/ShutdownWindow';
import { SocialWindow } from './components/windows/SocialWindow';
import { StoryWindow } from './components/windows/StoryWindow';
import { WelcomeWindow } from './components/windows/WelcomeWindow';
import { SITE } from './config/site';
import { createFallbackProjects } from './data/fallbackProjects';
import { loadProjects } from './lib/projects';
import { navigate, parseRoute, type AppRoute } from './lib/routing';
import { WindowManagerProvider, useWindowManager } from './lib/windowManager';
import type { ProjectManifest } from './types/project';
import type { ManagedWindow, OpenWindowInput } from './types/window';

const WINDOW_DEFINITIONS = {
  welcome: {
    id: 'welcome',
    kind: 'welcome',
    title: 'Welcome',
    icon: 'computer',
    width: 670,
    height: 530,
    minWidth: 430,
    minHeight: 390,
  },
  explorer: {
    id: 'explorer',
    kind: 'explorer',
    title: 'My Projects',
    icon: 'folder',
    width: 1060,
    height: 680,
    minWidth: 720,
    minHeight: 460,
  },
  about: {
    id: 'about',
    kind: 'about',
    title: 'About Ivan Sivák',
    icon: 'info',
    width: 700,
    height: 600,
    minWidth: 480,
    minHeight: 420,
  },
  story: {
    id: 'story',
    kind: 'story',
    title: 'My Story - WordPad',
    icon: 'document',
    width: 780,
    height: 680,
    minWidth: 500,
    minHeight: 420,
  },
  social: {
    id: 'social',
    kind: 'social',
    title: 'Network Neighborhood',
    icon: 'network',
    width: 680,
    height: 530,
    minWidth: 460,
    minHeight: 360,
  },
  recycle: {
    id: 'recycle',
    kind: 'recycle',
    title: 'Recycle Bin',
    icon: 'recycle',
    width: 650,
    height: 500,
    minWidth: 430,
    minHeight: 330,
  },
  settings: {
    id: 'settings',
    kind: 'settings',
    title: 'Display Properties',
    icon: 'settings',
    width: 520,
    height: 580,
    minWidth: 430,
    minHeight: 500,
    resizable: false,
  },
  shutdown: {
    id: 'shutdown',
    kind: 'shutdown',
    title: 'Shut Down Windows',
    icon: 'shutdown',
    width: 500,
    height: 300,
    minWidth: 500,
    minHeight: 300,
    resizable: false,
  },
} satisfies Record<string, OpenWindowInput>;

function projectWindowDefinition(project: ProjectManifest): OpenWindowInput {
  return {
    id: `project:${project.slug}`,
    kind: 'project',
    title: project.title,
    icon: project.slug === 'raytracer' ? 'raytracer' : 'project',
    width: project.window?.width ?? 1180,
    height: project.window?.height ?? 780,
    minWidth: project.window?.minWidth ?? 680,
    minHeight: project.window?.minHeight ?? 460,
    resizable: project.window?.resizable ?? true,
    projectSlug: project.slug,
  };
}

function Workstation() {
  const manager = useWindowManager();
  const [projects, setProjects] = createSignal<ProjectManifest[]>(createFallbackProjects(SITE.projectsOrigin));
  const [registrySource, setRegistrySource] = createSignal<'remote' | 'fallback'>('fallback');
  const [registryWarning, setRegistryWarning] = createSignal<string>();
  const [registryLoading, setRegistryLoading] = createSignal(true);
  const [startOpen, setStartOpen] = createSignal(false);
  const [routeVersion, setRouteVersion] = createSignal(0);

  const publishedProjects = createMemo(() => projects().filter((project) => project.status === 'published'));
  const projectBySlug = (slug?: string) => projects().find((project) => project.slug === slug);

  function open(input: OpenWindowInput) {
    manager.openWindow(input);
    setStartOpen(false);
  }

  function openWelcome(updateRoute = true) {
    open(WINDOW_DEFINITIONS.welcome);
    if (updateRoute) navigate({ kind: 'home' });
  }

  function openProjects(updateRoute = true) {
    open(WINDOW_DEFINITIONS.explorer);
    if (updateRoute) navigate({ kind: 'projects' });
  }

  function openAbout(updateRoute = true) {
    open(WINDOW_DEFINITIONS.about);
    if (updateRoute) navigate({ kind: 'about' });
  }

  function openStory(updateRoute = true) {
    open(WINDOW_DEFINITIONS.story);
    if (updateRoute) navigate({ kind: 'story' });
  }

  function openSocial() {
    open(WINDOW_DEFINITIONS.social);
  }

  function openRecycleBin() {
    open(WINDOW_DEFINITIONS.recycle);
  }

  function openSettings() {
    open(WINDOW_DEFINITIONS.settings);
  }

  function openShutdown() {
    open(WINDOW_DEFINITIONS.shutdown);
  }

  function openProject(project: ProjectManifest, updateRoute = true) {
    open(projectWindowDefinition(project));
    if (updateRoute) navigate({ kind: 'project', slug: project.slug });
  }

  function routeForWindow(item: ManagedWindow): AppRoute | undefined {
    switch (item.kind) {
      case 'explorer':
        return { kind: 'projects' };
      case 'project':
        return item.projectSlug ? { kind: 'project', slug: item.projectSlug } : undefined;
      case 'about':
        return { kind: 'about' };
      case 'story':
        return { kind: 'story' };
      case 'welcome':
        return { kind: 'home' };
      default:
        return undefined;
    }
  }

  function closeWindow(item: ManagedWindow) {
    manager.closeWindow(item.id);
    const route = routeForWindow(item);
    if (!route) return;

    const current = parseRoute();
    const matches = current.kind === route.kind && (current.kind !== 'project' || route.kind !== 'project' || current.slug === route.slug);
    if (matches) navigate({ kind: 'home' }, true);
  }

  function applyRoute(route: AppRoute) {
    switch (route.kind) {
      case 'home':
        if (manager.windows.length === 0) openWelcome(false);
        break;
      case 'projects':
        openProjects(false);
        break;
      case 'about':
        openAbout(false);
        break;
      case 'story':
        openStory(false);
        break;
      case 'project': {
        const project = projectBySlug(route.slug);
        if (project) openProject(project, false);
        else {
          // A fallback project may have opened while the live catalog was loading.
          // Remove that stale window if the authoritative registry does not contain it.
          manager.closeWindow(`project:${route.slug}`);
          openProjects(false);
          setRegistryWarning(`The project “${route.slug}” is not present in the published catalog.`);
        }
        break;
      }
    }
  }

  const onPopState = () => setRouteVersion((value) => value + 1);

  onMount(() => {
    window.addEventListener('popstate', onPopState);
    void loadProjects(SITE.projectsOrigin).then((loaded) => {
      setProjects(loaded.projects);
      setRegistrySource(loaded.source);
      setRegistryWarning(loaded.warning);
      setRegistryLoading(false);
    });
  });
  onCleanup(() => window.removeEventListener('popstate', onPopState));

  createEffect(() => {
    routeVersion();
    projects(); // Re-evaluate deep links when the live catalog replaces the fallback.
    untrack(() => applyRoute(parseRoute()));
  });

  createEffect(() => {
    routeVersion();
    const titleRoute = parseRoute();
    if (titleRoute.kind === 'project') {
      const project = projectBySlug(titleRoute.slug);
      document.title = project ? `${project.title} — ${SITE.owner}` : SITE.title;
    } else if (titleRoute.kind === 'projects') {
      document.title = `Projects — ${SITE.owner}`;
    } else if (titleRoute.kind === 'about') {
      document.title = `About — ${SITE.owner}`;
    } else if (titleRoute.kind === 'story') {
      document.title = `My Story — ${SITE.owner}`;
    } else {
      document.title = SITE.title;
    }
  });

  return (
    <div
      class="workstation"
      onClick={() => setStartOpen(false)}
      onContextMenu={(event) => {
        if ((event.target as HTMLElement).closest('.desktop')) event.preventDefault();
      }}
    >
      <Desktop
        projects={projects()}
        openProjects={openProjects}
        openAbout={openAbout}
        openStory={openStory}
        openSocial={openSocial}
        openRecycleBin={openRecycleBin}
        openProject={openProject}
      />

      <div class="window-layer" aria-live="polite">
        <For each={manager.windows}>
          {(item) => {
            const project = () => projectBySlug(item.projectSlug);
            return (
              <WindowFrame
                window={item}
                active={manager.activeWindowId() === item.id}
                onClose={() => closeWindow(item)}
              >
                <Switch>
                  <Match when={item.kind === 'welcome'}>
                    <WelcomeWindow
                      projectCount={publishedProjects().length}
                      registrySource={registrySource()}
                      registryWarning={registryWarning()}
                      registryLoading={registryLoading()}
                      openProjects={openProjects}
                      openAbout={openAbout}
                      openStory={openStory}
                    />
                  </Match>
                  <Match when={item.kind === 'explorer'}>
                    <ProjectExplorerWindow projects={projects()} openProject={openProject} />
                  </Match>
                  <Match when={item.kind === 'about'}><AboutWindow /></Match>
                  <Match when={item.kind === 'story'}><StoryWindow /></Match>
                  <Match when={item.kind === 'social'}><SocialWindow /></Match>
                  <Match when={item.kind === 'recycle'}>
                    <RecycleBinWindow projects={projects()} openProject={openProject} />
                  </Match>
                  <Match when={item.kind === 'settings'}>
                    <SettingsWindow close={() => manager.closeWindow(item.id)} />
                  </Match>
                  <Match when={item.kind === 'shutdown'}>
                    <ShutdownWindow cancel={() => manager.closeWindow(item.id)} />
                  </Match>
                  <Match when={item.kind === 'project' && project()}>
                    <ProjectWindow project={project()!} />
                  </Match>
                </Switch>
              </WindowFrame>
            );
          }}
        </For>
      </div>

      <Show when={registryLoading()}>
        <div class="boot-indicator window">
          <div class="window-body">
            <PixelIconProxy />
            <span>Reading project catalog...</span>
          </div>
        </div>
      </Show>

      <StartMenu
        open={startOpen()}
        projects={projects()}
        close={() => setStartOpen(false)}
        openProjects={openProjects}
        openProject={openProject}
        openAbout={openAbout}
        openStory={openStory}
        openSocial={openSocial}
        openSettings={openSettings}
        openShutdown={openShutdown}
      />
      <Taskbar startOpen={startOpen()} toggleStart={() => setStartOpen((value) => !value)} />
    </div>
  );
}

function PixelIconProxy() {
  return <span class="boot-computer" aria-hidden="true">▣</span>;
}

export default function App() {
  return (
    <WindowManagerProvider>
      <Workstation />
    </WindowManagerProvider>
  );
}
